import { PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Modal, Popover, Select, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import type { Dayjs } from "dayjs"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { DATE_FORMAT } from "../../../../../utils/date/date.ts"
import { LeadOverlayPill } from "../../../../Leads/components/LeadOverlayPill.tsx"
import {
  OPERATIONAL_STATES,
  OPERATIONAL_STATE_LABELS,
  type OperationalState,
  type StoredOperationalState,
  isLiveOverlay
} from "../../../constants/operational-states.ts"
import {
  addedStates,
  fieldsRequiredByStates
} from "../../../helpers/operational-state-requirements.ts"
import { type NeedsInfoItem } from "../../../types/lead.types.ts"
import { LeadScheduleFields } from "../../schedule/LeadScheduleFields.tsx"
import { FOLLOW_UP_DEFAULT_MINUTES } from "../../schedule/schedule-durations.ts"
import { NeedsInfoFields } from "./NeedsInfoFields.tsx"
import { NeedsInfoStatusPopover } from "./NeedsInfoStatusPopover.tsx"
import { type LeadHeaderPatch } from "./lead-header-patch.ts"
import { useNeedsInfoEditor } from "./needs-info-editor-context.tsx"
import {
  buildNeedsInfoItems,
  needsInfoFormValueFrom,
  type NeedsInfoFormValue,
  sameNeedsInfoItems
} from "./needs-info-form.ts"

const { Text } = Typography

const TIME_FORMAT = "HH:mm"

const stateOptions = Object.values(OPERATIONAL_STATES).map((value) => ({
  value,
  label: OPERATIONAL_STATE_LABELS[value]
}))

interface StatesFormValues {
  operational_states?: OperationalState[]
  follow_up_owner?: string
  date?: Dayjs
  time?: Dayjs
  duration?: number
  needs_info?: NeedsInfoFormValue
}

interface LeadWorkspaceStatesProps {
  states?: StoredOperationalState[] | null
  followUpDate?: string | null
  needsInfoItems?: NeedsInfoItem[] | null
  owners: BaseOptionType[]
  canEdit?: boolean
  saving?: boolean
  testId?: string
  onChange: (patch: LeadHeaderPatch) => Promise<boolean>
}

export function LeadWorkspaceStates({
  states,
  followUpDate,
  needsInfoItems,
  owners,
  canEdit,
  saving,
  testId = "lead-header-states",
  onChange
}: LeadWorkspaceStatesProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<StatesFormValues>()
  const [open, setOpen] = useState(false)
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false)
  const { registerOpener } = useNeedsInfoEditor()

  const saved = useMemo(() => states ?? [], [states])

  const current = useMemo(() => saved.filter(isLiveOverlay), [saved])

  // The picker cannot show a retired parking overlay, so a submission carrying
  // only what it holds would un-park the row as a side effect of an unrelated
  // edit. Retired values keep their original slot, as in LeadOperationalStatesCell.
  const withRetiredKept = (picked: OperationalState[]) => {
    const kept = saved.filter(
      (state) => !isLiveOverlay(state) || picked.includes(state)
    )
    return [...kept, ...picked.filter((state) => !kept.includes(state))]
  }

  const selected = Form.useWatch("operational_states", form)

  // Mirrors the backend: only the overlays this submission adds are enforced,
  // so an overlay that predates the rule does not block an unrelated edit.
  const requiredByStates = useMemo(
    () => fieldsRequiredByStates(addedStates(selected, current)),
    [selected, current]
  )

  const flaggingFollowUp = !!selected?.includes(OPERATIONAL_STATES.FOLLOW_UP)

  const owner = Form.useWatch("follow_up_owner", form)
  const date = Form.useWatch("date", form)
  const time = Form.useWatch("time", form)

  // Flagging a lead for follow-up books nothing, so the slot stays optional —
  // but half a slot is worse than none: the server refuses a date without a
  // time, and a date and time with nobody to own them schedules no meeting.
  // Touch any one of the three and all three are owed.
  const scheduling = !!(owner || date || time)

  const required = scheduling || requiredByStates.size > 0

  const showNeedsInfo = !!selected?.includes(OPERATIONAL_STATES.NEEDS_INFO)

  // Items left behind by a client that dropped the overlay without clearing
  // them must not pre-fill the form.
  const persistedNeedsInfoItems = useMemo(
    () =>
      current.includes(OPERATIONAL_STATES.NEEDS_INFO)
        ? (needsInfoItems ?? [])
        : [],
    [current, needsInfoItems]
  )

  const resetFormToPersisted = useCallback(() => {
    form.setFieldsValue({
      operational_states: current,
      duration: FOLLOW_UP_DEFAULT_MINUTES,
      needs_info: needsInfoFormValueFrom(persistedNeedsInfoItems)
    })
  }, [form, current, persistedNeedsInfoItems])

  const openEditor = useCallback(() => {
    resetFormToPersisted()
    setOpen(true)
  }, [resetFormToPersisted])

  useEffect(() => {
    registerOpener(openEditor)
    return () => registerOpener(null)
  }, [registerOpener, openEditor])

  const submit = async () => {
    let values: StatesFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    const stillNeedsInfo = !!values.operational_states?.includes(
      OPERATIONAL_STATES.NEEDS_INFO
    )
    const previouslyNeedsInfo = current.includes(OPERATIONAL_STATES.NEEDS_INFO)
    const nextNeedsInfoItems = buildNeedsInfoItems(values.needs_info)
    const needsInfoChanged =
      stillNeedsInfo &&
      (!previouslyNeedsInfo ||
        !sameNeedsInfoItems(nextNeedsInfoItems, persistedNeedsInfoItems))

    const persisted = await onChange({
      operational_states: withRetiredKept(values.operational_states ?? []),
      ...(needsInfoChanged ? { needs_info_items: nextNeedsInfoItems } : {}),
      ...(values.follow_up_owner
        ? { follow_up_owner: values.follow_up_owner }
        : {}),
      ...(values.date
        ? { follow_up_date: values.date.format(DATE_FORMAT) }
        : {}),
      ...(values.time
        ? {
            follow_up_time: values.time.format(TIME_FORMAT),
            follow_up_duration_minutes: values.duration
          }
        : {})
    })

    if (!persisted) return

    setOpen(false)
    resetFormToPersisted()
  }

  return (
    <Flex gap={4} align={"center"} wrap data-testid={testId}>
      {current.length ? (
        current.map((state) =>
          state === OPERATIONAL_STATES.NEEDS_INFO ? (
            <Popover
              key={state}
              trigger={"click"}
              open={statusPopoverOpen}
              onOpenChange={setStatusPopoverOpen}
              content={
                <NeedsInfoStatusPopover
                  items={needsInfoItems ?? []}
                  canEdit={canEdit}
                  onEdit={() => {
                    setStatusPopoverOpen(false)
                    openEditor()
                  }}
                />
              }
            >
              <LeadOverlayPill
                state={state}
                followUpDate={followUpDate}
                className={"cursor-pointer"}
              />
            </Popover>
          ) : (
            <LeadOverlayPill
              key={state}
              state={state}
              followUpDate={followUpDate}
            />
          )
        )
      ) : (
        <Text type={"secondary"}>{t("leadWorkspace.header.states.none")}</Text>
      )}

      {canEdit && (
        <>
          <Button
            size={"small"}
            type={"link"}
            icon={<PlusOutlined />}
            onClick={openEditor}
          >
            {t("leadWorkspace.header.states.add")}
          </Button>

          <Modal
            open={open}
            destroyOnHidden
            maskClosable={false}
            title={t("leadWorkspace.header.states.title")}
            okText={t("leadWorkspace.header.save")}
            cancelText={t("leadWorkspace.header.cancel")}
            confirmLoading={saving}
            onOk={submit}
            onCancel={() => {
              setOpen(false)
              resetFormToPersisted()
            }}
          >
            <Form form={form} layout={"vertical"}>
              <Form.Item<StatesFormValues>
                name={"operational_states"}
                label={t("leadWorkspace.header.states.label")}
              >
                <Select
                  mode={"multiple"}
                  allowClear
                  optionFilterProp={"label"}
                  options={stateOptions}
                  placeholder={t("leadWorkspace.header.states.placeholder")}
                />
              </Form.Item>

              <NeedsInfoFields visible={showNeedsInfo} />

              <LeadScheduleFields
                kind={"followUp"}
                owners={owners}
                required={required}
                ownerClearable
                showOwner={flaggingFollowUp}
                showDate={flaggingFollowUp}
                showTime={flaggingFollowUp}
              />
            </Form>
          </Modal>
        </>
      )}
    </Flex>
  )
}
